import type { PathPoint } from '../data/map';
import { ROAD_MAX_HALF_WIDTH, isWithinRoadRibbon } from './RoadGeometry';

/**
 * One placement contract for authored pads, generated sockets and player builds.
 *
 * A field socket is a ground-level turret foundation, not a centre point. Its
 * complete footprint and an extra service shoulder must stay outside every
 * route ribbon. This deliberately uses the widest rendered road rather than a
 * local visual width so joins, alternate lanes and projected maps are safe.
 */
export const FIELD_TURRET_FOOTPRINT_RADIUS = 22;
// Keep only a small service margin beyond the sprite footprint so authored
// pads can sit close to bends without allowing a turret to overlap the road.
export const FIELD_TURRET_SERVICE_SHOULDER = 4;
export const FIELD_TURRET_ROUTE_EXCLUSION =
  ROAD_MAX_HALF_WIDTH + FIELD_TURRET_FOOTPRINT_RADIUS + FIELD_TURRET_SERVICE_SHOULDER;

/** Returns true only when a ground-level field turret clears every route. */
export function isFieldTurretPlacementSafe(point: PathPoint, routes: PathPoint[][]): boolean {
  if (routes.length === 0) return true;
  // RoadGeometry owns the road-ribbon distance/intersection contract. Passing
  // the footprint-expanded radius catches centreline, shoulders and joins.
  return !isWithinRoadRibbon(
    point,
    routes,
    FIELD_TURRET_FOOTPRINT_RADIUS + FIELD_TURRET_SERVICE_SHOULDER,
  );
}

/** Filter authored or generated ground sockets without changing their source. */
export function filterSafeFieldPads<T extends PathPoint>(pads: readonly T[], routes: PathPoint[][]): T[] {
  return pads.filter((pad) => isFieldTurretPlacementSafe(pad, routes));
}
